import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import HomeTestimonials from '../components/HomeTestimonials';
import './LandingV2.css';

/**
 * Landing V2: Student-Focused
 *
 * Theme: "Study Smarter, Not Harder"
 * Tone: Bold, modern, energetic, youth-focused
 * Target: Ethiopian students preparing for a high-stakes exam. Deliberately NOT
 *   written as ESSLCE-only — the audience widens next season and the copy
 *   should not need rewriting when it does. Two exams are named: the ESSLCE and
 *   the AAU UAT, both of which are live and sold. Do not name unannounced exams
 *   here.
 *
 * The AAU UAT is named in the hero, the features, the subject chips and the
 * steps — beside the ESSLCE, not in a section of its own. The switch-gated band
 * above the hero is the only UAT surface that can disappear; the copy below it
 * always names both exams, so a page a UAT ad points at never stops mentioning
 * the exam the ad was about.
 *
 * Key Messages (kept in sync with the blog's philosophy — see /blog):
 * - The floor: a question, its answer, an explanation, something to argue with
 * - In the language you think in (not literal translation)
 * - Real past papers, already structured, so no hunting for scans
 * - Free to start. No permanent-free promise anywhere on this page.
 */

/** What the UAT band shows. Everything a student is told about price and
 *  deadline comes from the server, so it can be corrected from the admin
 *  dashboard without a build. */
type UatAnnouncement = {
  announced: boolean;
  mode: string;
  package: {
    key: string;
    name: string;
    tagline: string | null;
    amount: number | null;
    currency: string;
    note: string | null;
  };
};

const LandingV2: React.FC = () => {
  const navigate = useNavigate();
  /**
   * The UAT announcement.
   *
   * Fetched rather than hardcoded because the price, the tagline and whether
   * this is announced at all must be changeable from the admin dashboard.
   * Copy compiled into a bundle takes a build to correct, which is how a wrong
   * price stays up for a day.
   *
   * `?uat_preview=1` reveals it while the switch is still off, so the whole
   * page can be walked on production, with real copy, before anyone sees it.
   */
  const [uat, setUat] = useState<UatAnnouncement | null>(null);
  useEffect(() => {
    const preview = new URLSearchParams(window.location.search).has('uat_preview');
    fetch(`/api/student/packages/uat/announcement${preview ? '?preview=true' : ''}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setUat(d?.announced ? d : null))
      .catch(() => setUat(null));
  }, []);

  /** Someone who arrived FOR the UAT must land in it after signing up, not on
   *  the ESSLCE dashboard. The intent outlives the redirect through login. */
  const startUat = () => {
    try {
      localStorage.setItem('lomi_intent_package', 'uat-aau');
    } catch {
      /* storage blocked — they land on the dashboard and can still pick it */
    }
    navigate('/login');
  };

  const [activeFeature, setActiveFeature] = useState(0);
  const [chatMessages, setChatMessages] = useState<{text: string, isLomi: boolean}[]>([]);
  const chatRef = useRef<HTMLDivElement>(null);

  // Drawn from what students actually wrote to us, not from a feature list.
  const features = [
    {
      icon: '📝',
      title: 'Past papers, with the answers',
      desc: 'Years of real ESSLCE past papers, and a full UAT bank for the AAU entrance test — every one with the answer and an explanation',
      demo: "Correct answer: B. Here's why — and here's the trap the other options are setting."
    },
    {
      icon: '💬',
      title: "Argue with it until it clicks",
      desc: "It doesn't just hand you the answer. Ask again, ask differently, say you still don't get it",
      demo: "Still not landing? Let me try it a completely different way — forget the formula for a second."
    },
    {
      icon: '🇪🇹',
      title: 'In the language you think in',
      desc: 'Not translation — an explanation the way a classmate would give it, Amharic and English together',
      demo: "እሺ፣ በቀላሉ ልናገረው — the reaction needs chlorophyll ስለሆነ ነው መልሱ B የሆነው።"
    },
    {
      icon: '🎯',
      title: 'Knows where you are weak',
      desc: 'It tracks what you keep missing and brings it back until you have it',
      demo: "You've missed 3 questions on this topic. Let's fix it now, then I'll test you again tomorrow."
    },
  ];

  // Simulated chat demo
  const demoConversation = [
    { text: "Why is the answer B and not C?", isLomi: false },
    { text: "Great question! Let me break it down...\n\nOption C seems right because it mentions 'energy transfer', but B is correct because:\n\n✓ Chlorophyll specifically absorbs red and blue light\n✓ The question asks about the PRIMARY pigment\n\nThis is a common trap on the real exam!", isLomi: true },
    { text: "አመሰግናለው! That makes sense now 🙏", isLomi: false },
    { text: "በርታ! You're doing great. Ready for more practice questions? 💪", isLomi: true },
  ];

  // Animated chat demo
  useEffect(() => {
    const showMessages = async () => {
      for (let i = 0; i < demoConversation.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        setChatMessages(prev => [...prev, demoConversation[i]]);
      }
    };

    const timer = setTimeout(showMessages, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <div className="landing-v2">
      {/* Navigation */}
      <nav className="nav-v2">
        <div className="nav-brand-v2">
          <Logo height={40} withAmharic />
        </div>
        <div className="nav-links-v2">
          <button className="nav-link-v2" onClick={() => navigate('/about')}>
            About
          </button>
          <button className="nav-link-v2" onClick={() => navigate('/testimonials')}>
            Stories
          </button>
          <button className="nav-link-v2" onClick={() => navigate('/blog')}>
            Blog
          </button>
          <button className="nav-link-v2 nav-cta-v2" onClick={() => navigate('/login')}>
            Start Free →
          </button>
        </div>
      </nav>

      {/* The UAT band.
          Kept ABOVE the hero even though the page below now names both exams,
          because it is the only UAT surface that is above the fold. Someone who
          clicked an ad that said "AAU UAT" decides whether they are in the right
          place before they scroll, and the band answers that in one line.

          Still switch-gated, so the offer disappears the moment the launch is
          turned off — but only the offer. The hero, the features and the subject
          chips name the UAT unconditionally, so a page a UAT ad points at never
          stops mentioning the exam the ad was about.

          NO PRICE ON THIS PAGE, deliberately. The public page sells the free
          tier; the number is shown inside, once someone has seen the product.
          A figure in a paid ad's landing view is an objection raised before the
          value is, and it also pins the price to a deploy — the whole reason
          the copy is fetched. `announcement` still returns `amount`; nothing on
          this page renders it. */}
      {uat && (
        <section className="uat-band">
          <div className="uat-band__inner">
            <div className="uat-band__text">
              <p className="uat-band__eyebrow">🎓 New — Addis Ababa University</p>
              <h2 className="uat-band__title">
                Preparing for the UAT?
              </h2>
              <p className="uat-band__sub">
                {uat.package.tagline ||
                  'Questions with worked solutions, lessons across all three sections, and a full timed mock.'}
              </p>
            </div>
            <div className="uat-band__action">
              <button className="uat-band__cta" onClick={startUat}>
                Start free →
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Hero Section */}
      <section className="hero-v2">
        <div className="hero-content-v2">
          <div className="hero-eyebrow-v2">
            <span className="eyebrow-icon">⚡</span>
            Built for the ESSLCE &amp; the AAU UAT
          </div>

          <h1 className="hero-title-v2">
            <span className="title-line-1">Built to help you</span>
            <span className="title-line-2">pass the exam that decides everything.</span>
          </h1>

          <p className="hero-subtitle-v2">
            Meet <strong>Lomi (ሎሚ)</strong> — an AI tutor built on years of real
            ESSLCE past papers, and on a full package for the{' '}
            <strong>AAU Undergraduate Admission Test (UAT)</strong>. Every question
            comes with its answer, an explanation, and something you can argue with
            until it clicks — in English or Amharic.
          </p>

          <div className="hero-cta-v2">
            <button
              className="cta-primary-v2"
              onClick={() => navigate('/login')}
            >
              <span>Start Learning Free</span>
              <span className="cta-arrow">→</span>
            </button>
            <p className="cta-subtext">One tap — sign in with Telegram or Gmail</p>
            <a
              className="cta-secondary-telegram"
              href="https://t.me/lomistudy"
              target="_blank"
              rel="noopener noreferrer"
            >
              📢 Or get a daily question on Telegram — @lomistudy
            </a>
          </div>

          <div className="social-proof-v2">
            <div className="avatars">
              <div className="avatar">👩🏾</div>
              <div className="avatar">👨🏾</div>
              <div className="avatar">👩🏾</div>
              <div className="avatar">👨🏾</div>
              <div className="avatar more">+</div>
            </div>
            <p>Join students across Ethiopia — on the web and on Telegram</p>
          </div>
        </div>

        <div className="hero-demo-v2">
          <div className="phone-frame-v2">
            <div className="phone-notch"></div>
            <div className="phone-screen-v2">
              <div className="screen-header-v2">
                <span className="header-icon">🍋</span>
                <span>Lomi</span>
                <span className="header-amharic">ሎሚ</span>
                <span className="header-status">Online</span>
              </div>
              <div className="chat-container-v2" ref={chatRef}>
                {chatMessages.map((msg, i) => (
                  <div
                    key={i}
                    className={`chat-msg-v2 ${msg.isLomi ? 'lomi' : 'user'}`}
                  >
                    {msg.isLomi && <span className="msg-avatar">🍋</span>}
                    <div className="msg-content">{msg.text}</div>
                  </div>
                ))}
                {chatMessages.length < demoConversation.length && (
                  <div className="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                )}
              </div>
              <div className="chat-input-v2">
                <span className="input-placeholder">Ask Lomi anything...</span>
                <span className="input-send">↑</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Lomi does.
          This slot used to hold a problem/agitation block ("You solved the
          question. Now what?") that argued the student's situation was bad
          before saying what we sell. It is the first thing under the hero, so
          it is where paid traffic decides whether this is their exam and their
          product — and a rhetorical question is a slow way to answer that.
          Plain statement of what we do instead, both exams named. */}
      <section className="what-v2">
        <div className="what-content-v2">
          <h2 className="what-heading">
            What Lomi does
          </h2>
          <p className="what-reason">
            Lomi puts everything you need in one place. Years of real ESSLCE past papers,
            and full preparation for the AAU Undergraduate Admission Test across
            Quantitative, Verbal and Analytical — every question with the correct answer
            and a written explanation. Lessons on every topic: short notes, summaries,
            practice questions and quizzes. And when an explanation doesn't land, you ask
            again, in English or Amharic, until it does. Web and Telegram, one account.
          </p>
          <div className="what-statement">
            <span className="lomi-icon-inline">🍋</span>
            <span>One account. Both exams.</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-v2">
        <div className="features-header-v2">
          <h2>Why Students Love Lomi</h2>
          <p>In their words, not ours — the four things they wrote to us about most</p>
        </div>

        <div className="features-showcase-v2">
          <div className="feature-tabs-v2">
            {features.map((feature, i) => (
              <button
                key={i}
                className={`feature-tab ${activeFeature === i ? 'active' : ''}`}
                onClick={() => setActiveFeature(i)}
              >
                <span className="tab-icon">{feature.icon}</span>
                <span className="tab-title">{feature.title}</span>
              </button>
            ))}
          </div>

          <div className="feature-detail-v2">
            <div className="feature-card-v2">
              <div className="feature-icon-large">{features[activeFeature].icon}</div>
              <h3>{features[activeFeature].title}</h3>
              <p>{features[activeFeature].desc}</p>
              <div className="feature-demo-bubble">
                <span className="demo-avatar">🍋</span>
                <span className="demo-text">{features[activeFeature].demo}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modes Section — One Tutor, Four Modes */}
      <section className="modes-v2">
        <div className="modes-header-v2">
          <h2>One Tutor. Four Modes.</h2>
          <p>Lomi adapts to how you want to study right now.</p>
        </div>
        <div className="modes-grid-v2">
          <div className="mode-card-v2">
            <div className="mode-icon-v2">📚</div>
            <h3>Learn</h3>
            <p>Step-by-step explanations. Patient and thorough — for when you need a concept broken down.</p>
          </div>
          <div className="mode-card-v2">
            <div className="mode-icon-v2">🧠</div>
            <h3>Revision</h3>
            <p>Lomi asks you questions instead of giving answers. Trains you to think through problems on your own.</p>
          </div>
          <div className="mode-card-v2">
            <div className="mode-icon-v2">⏱️</div>
            <h3>Assessment</h3>
            <p>Exam conditions. Minimal hints. Simulates what exam day actually feels like.</p>
          </div>
          <div className="mode-card-v2">
            <div className="mode-icon-v2">⚡</div>
            <h3>Speed</h3>
            <p>Quick focused review — when you just need the key point, not the whole lecture.</p>
          </div>
        </div>
      </section>

      {/* Subjects Section */}
      <section className="subjects-v2">
        <h2>Both Exams. Every Subject. One Tutor.</h2>
        <p className="subjects-lead">ESSLCE</p>
        <div className="subjects-grid-v2">
          <div className="subject-chip">🧬 Biology</div>
          <div className="subject-chip">🧪 Chemistry</div>
          <div className="subject-chip">⚛️ Physics</div>
          <div className="subject-chip">🌍 Geography</div>
          <div className="subject-chip">📜 History</div>
          <div className="subject-chip">📊 Economics</div>
          <div className="subject-chip">🔢 Mathematics</div>
          <div className="subject-chip">📚 English</div>
        </div>
        <p className="subjects-note">Aligned with Ethiopian Grade 11 & 12 curriculum</p>

        <p className="subjects-lead subjects-lead--second">AAU UAT</p>
        <div className="subjects-grid-v2">
          <div className="subject-chip">🔢 Quantitative</div>
          <div className="subject-chip">📖 Verbal</div>
          <div className="subject-chip">🧩 Analytical</div>
        </div>
        <p className="subjects-note">
          The three sections of the Addis Ababa University entrance test
        </p>
      </section>

      {/* How It Works */}
      <section className="how-v2">
        <h2>Get Started in 2 Minutes</h2>
        <div className="steps-v2">
          <div className="step-v2">
            <div className="step-number">1</div>
            <div className="step-icon">📱</div>
            <h3>Sign up free</h3>
            <p>One tap with Telegram or Gmail</p>
          </div>
          <div className="step-connector">→</div>
          <div className="step-v2">
            <div className="step-number">2</div>
            <div className="step-icon">📚</div>
            <h3>Pick your exam</h3>
            <p>An ESSLCE subject, or the AAU UAT — start where you struggle most</p>
          </div>
          <div className="step-connector">→</div>
          <div className="step-v2">
            <div className="step-number">3</div>
            <div className="step-icon">🚀</div>
            <h3>Start learning</h3>
            <p>Ask Lomi anything — get instant help</p>
          </div>
        </div>
      </section>

      {/* Platforms Section — Web + Telegram */}
      <section className="platforms-v2">
        <div className="platforms-header-v2">
          <h2>Study on the web. Practice on Telegram.</h2>
          <p>Same account, same progress. Switch between them whenever you want.</p>
        </div>
        <div className="platforms-grid-v2">
          <div className="platform-card-v2">
            <div className="platform-icon-v2">💻</div>
            <h3>lomistudy.com</h3>
            <p>The full experience. Best for long study sessions, deep lessons, and tracking progress across subjects.</p>
            <button
              className="platform-cta-v2"
              onClick={() => navigate('/login')}
            >
              Start on Web →
            </button>
          </div>
          <div className="platform-card-v2">
            <div className="platform-icon-v2">💬</div>
            <h3>@lomistudybot</h3>
            <p>Quick practice on your phone. Perfect for 10-minute drills between classes.</p>
            <a
              className="platform-cta-v2"
              href="https://t.me/lomistudybot"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open on Telegram →
            </a>
          </div>
        </div>
      </section>

      {/* Student voices */}
      <HomeTestimonials />
      <div className="stories-cta-v2">
        <button className="stories-cta-link" onClick={() => navigate('/testimonials')}>
          Read what students wrote →
        </button>
      </div>

      {/* Final CTA */}
      <section className="final-cta-v2">
        <div className="cta-card-v2">
          <h2>Ready to start?</h2>
          <p>
            Join students across Ethiopia preparing for the ESSLCE and the AAU UAT
          </p>
          <button
            className="cta-primary-v2 large"
            onClick={() => navigate('/login')}
          >
            Start Learning Free →
          </button>
          <p className="cta-guarantee">One tap — sign in with Telegram or Gmail</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-v2">
        <div className="footer-content-v2">
          <div className="footer-brand-v2">
            <Logo variant="dark" height={32} withAmharic />
          </div>
          <p className="footer-tagline-v2">Fresh learning. Clear understanding.</p>

          <div className="footer-links-v2">
            <button onClick={() => navigate('/about')}>About</button>
            <button onClick={() => navigate('/testimonials')}>Stories</button>
            <button onClick={() => navigate('/blog')}>Blog</button>
            <button onClick={() => navigate('/careers')}>Careers</button>
            <button onClick={() => navigate('/contact')}>Contact</button>
            <a href="https://t.me/Lomistudybot" target="_blank" rel="noopener noreferrer">Telegram</a>
            <button onClick={() => navigate('/privacy')}>Privacy</button>
            <button onClick={() => navigate('/terms')}>Terms</button>
          </div>

          <div className="footer-company-v2">
            <p>Built with ❤️ in Ethiopia</p>
            <p className="copyright-v2">© {new Date().getFullYear()} Lomi. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingV2;
